import { useEffect, useState } from "react";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP } from "@/__main__/constants.mjs";
import router, { EVENT_CHANGE_ROUTE } from "@/__main__/router.mjs";
import gamesList from "@/app/games.mjs";
import gameRoutes from "@/routes/game.mjs";
import SymbolMap from "@/util/symbol-map.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

let lastGameSymbol: symbol;

/**
 * This is used to find the current game symbol, it does not update.
 * For usage in React components, you should use the `useGameSymbol` hook,
 * which handles updates. This should not be used with `globals.location`,
 * as this does not normalize the path to exclude app version.
 *
 * @param pathname - Defaults to router.route?.path or router.lastPathname if undefined. Prefer defaults where possible.
 * @returns Current game symbol based on current route or undefined if none
 */
export const findGameSymbol = (
  pathname: string | RegExp = router.route?.path ?? router.lastPathname,
) => {
  const gameSymbol = !pathname
    ? undefined
    : SymbolMap.keys(gameRoutes).find((gameSymbol) =>
        gameRoutes[gameSymbol].find(({ path }) => {
          if (!path) return false;
          if (pathname === path) return true;
          if (path instanceof RegExp && typeof pathname === "string")
            return path.test(pathname);
          return false;
        }),
      );
  if (gameSymbol) lastGameSymbol = gameSymbol;
  return gameSymbol;
};

// Return the most recent valid game symbol.
export const getCachedGameSymbol = () => lastGameSymbol;

/**
 * Optimized to not trigger re-render.
 *
 * @returns Returns game symbol based current route
 */
export const useGameSymbol = () => {
  const [gameSymbol, setGameSymbol] = useState(findGameSymbol());
  useEffect(() => {
    const listener = () => {
      setGameSymbol(findGameSymbol());
    };
    router.events.on(EVENT_CHANGE_ROUTE, listener);
    return () => {
      router.events.off(EVENT_CHANGE_ROUTE, listener);
    };
  }, []);
  return gameSymbol;
};

export const useGameInfo = () => {
  const { volatile, localState } = useSnapshot(readState);
  const gameSymbol = localState.currentGame || volatile.currentGame;

  return gamesList.find((game) => game.gameSymbol === gameSymbol);
};

/**
 *
 * @returns Returns game definition based on the current route (via useGameSymbol)
 */
export const useCurrentGame = () => {
  const gameSymbol = useGameSymbol();
  const { localState, volatile } = useSnapshot(readState);

  if (IS_APP) {
    return gameSymbol || localState?.currentGame;
  }
  return gameSymbol || volatile.currentGame;
};
